import { useIntl } from 'react-intl';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@immo/ui-kit';
import {
  WHATSAPP_OPERATOR_LIST_MAX_LIMIT,
  type WhatsAppFailedDispatch,
  type WhatsAppUnmatchedMessage,
} from '@immo/api-contract';
import { useCan } from '@/lib/auth/permissions';
import { useTranslations } from '@/i18n/use-translations';
import { ARIA_SORT, SortableHeader, useTableSort, type SortValue } from '@/components/sortable-table';

export type WhatsAppList<TRow> = { readonly items: ReadonlyArray<TRow>; readonly truncated: boolean } | null;

const DASH = '—';

export const whatsAppReasonKey = (token: string): string => `reason.${token.toLowerCase().replaceAll('_', '-')}`;

const FAILED_FIELDS = ['recipient', 'reason', 'provider-code', 'sent-by', 'occurred-at'] as const;
type FailedField = (typeof FAILED_FIELDS)[number];

const UNMATCHED_FIELDS = ['phone', 'name', 'body', 'received-at', 'reason'] as const;
type UnmatchedField = (typeof UNMATCHED_FIELDS)[number];

type WhatsAppSectionProps = {
  readonly failed: WhatsAppList<WhatsAppFailedDispatch>;
  readonly unmatched: WhatsAppList<WhatsAppUnmatchedMessage>;
};

export function WhatsAppSection({ failed, unmatched }: WhatsAppSectionProps) {
  const t = useTranslations('pages.admin.workspaces.detail.whatsapp');
  const canRead = useCan('backoffice:communications:whatsapp-dispatch-read');

  if (!canRead) return null;

  return (
    <section className="space-y-6">
      <SectionHeading>{t('section')}</SectionHeading>
      <FailedPanel list={failed} />
      <UnmatchedPanel list={unmatched} />
    </section>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <h2 className="text-sm font-semibold uppercase tracking-wide text-text-tertiary">{children}</h2>;
}

function PanelNotices({ list, empty }: { readonly list: NonNullable<WhatsAppList<unknown>>; readonly empty: string }) {
  const t = useTranslations('pages.admin.workspaces.detail.whatsapp');

  return (
    <>
      {list.truncated && (
        <p className="text-sm text-text-tertiary">{t('truncated', { limit: WHATSAPP_OPERATOR_LIST_MAX_LIMIT })}</p>
      )}
      {list.items.length === 0 && <p className="text-text-tertiary">{empty}</p>}
    </>
  );
}

function FailedPanel({ list }: { readonly list: WhatsAppList<WhatsAppFailedDispatch> }) {
  const t = useTranslations('pages.admin.workspaces.detail.whatsapp');
  const intl = useIntl();
  const items = list?.items ?? [];
  const sortKey = (row: WhatsAppFailedDispatch, field: FailedField): SortValue => {
    switch (field) {
      case 'recipient':
        return row.recipientPhone;
      case 'reason':
        return t(whatsAppReasonKey(row.failureReason));
      case 'provider-code':
        return row.providerErrorCode;
      case 'sent-by':
        return row.sentByMemberName;
      case 'occurred-at':
        return Date.parse(row.occurredAt);
    }
  };

  const { sorted, sort, toggleSort } = useTableSort(items, sortKey);

  return (
    <div className="space-y-2">
      <h3 className="text-base font-medium text-text-primary">{t('failed.heading')}</h3>
      {list === null ? (
        <p className="text-sm text-danger-600">{t('load-error')}</p>
      ) : (
        <>
          <PanelNotices list={list} empty={t('failed.empty')} />
          {items.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  {FAILED_FIELDS.map((field) => (
                    <TableHead
                      key={field}
                      {...(sort !== null && sort.field === field ? { 'aria-sort': ARIA_SORT[sort.direction] } : {})}
                    >
                      <SortableHeader
                        field={field}
                        label={t(`failed.${field}`)}
                        ariaLabel={t('sort-label', { column: t(`failed.${field}`) })}
                        sort={sort}
                        onSort={toggleSort}
                      />
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-mono text-sm">{row.recipientPhone}</TableCell>
                    <TableCell>{t(whatsAppReasonKey(row.failureReason))}</TableCell>
                    <TableCell className="max-w-0">
                      {row.providerErrorCode === null ? (
                        <span className="text-text-placeholder">{DASH}</span>
                      ) : (
                        <span className="block truncate" title={row.providerErrorDetail ?? undefined}>
                          {row.providerErrorCode}
                          {row.providerErrorDetail !== null && (
                            <span className="text-text-tertiary"> — {row.providerErrorDetail}</span>
                          )}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>{row.sentByMemberName}</TableCell>
                    <TableCell>
                      {intl.formatDate(row.occurredAt, { dateStyle: 'medium', timeStyle: 'short' })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </>
      )}
    </div>
  );
}

function UnmatchedPanel({ list }: { readonly list: WhatsAppList<WhatsAppUnmatchedMessage> }) {
  const t = useTranslations('pages.admin.workspaces.detail.whatsapp');
  const intl = useIntl();
  const items = list?.items ?? [];
  const sortKey = (row: WhatsAppUnmatchedMessage, field: UnmatchedField): SortValue => {
    switch (field) {
      case 'phone':
        return row.senderPhone;
      case 'name':
        return row.profileName;
      case 'body':
        return row.body;
      case 'received-at':
        return Date.parse(row.receivedAt);
      case 'reason':
        return t(whatsAppReasonKey(row.reason));
    }
  };

  const { sorted, sort, toggleSort } = useTableSort(items, sortKey);

  return (
    <div className="space-y-2">
      <h3 className="text-base font-medium text-text-primary">{t('unmatched.heading')}</h3>
      <p className="text-sm text-text-tertiary">{t('unmatched.scope-note')}</p>
      {list === null ? (
        <p className="text-sm text-danger-600">{t('load-error')}</p>
      ) : (
        <>
          <PanelNotices list={list} empty={t('unmatched.empty')} />
          {items.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  {UNMATCHED_FIELDS.map((field) => (
                    <TableHead
                      key={field}
                      {...(sort !== null && sort.field === field ? { 'aria-sort': ARIA_SORT[sort.direction] } : {})}
                    >
                      <SortableHeader
                        field={field}
                        label={t(`unmatched.${field}`)}
                        ariaLabel={t('sort-label', { column: t(`unmatched.${field}`) })}
                        sort={sort}
                        onSort={toggleSort}
                      />
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-mono text-sm">{row.senderPhone}</TableCell>
                    <TableCell className={row.profileName === null ? 'text-text-placeholder' : undefined}>
                      {row.profileName ?? DASH}
                    </TableCell>
                    <TableCell className="max-w-0">
                      <span className="block truncate" title={row.body}>
                        {row.body}
                      </span>
                    </TableCell>
                    <TableCell>
                      {intl.formatDate(row.receivedAt, { dateStyle: 'medium', timeStyle: 'short' })}
                    </TableCell>
                    <TableCell>{t(whatsAppReasonKey(row.reason))}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </>
      )}
    </div>
  );
}
